import React, {useEffect, useRef, useState} from 'react';
import CardHeader from '../../components/common/card-header/CardHeader';
import {
  Card,
  FlexItem,
  FlexLayout, FormField, FormFieldHelperText, FormFieldLabel, Input, Link, Spinner,
  StackLayout, Text
} from '@salt-ds/core';

import Constants from '../../components/common/Constants';
import '../CommonCard.css';
import {useLocation, useNavigate} from 'react-router';
import PageNavigation from '../../components/common/page-navigation/PageNavigation';
import CardAlert from '../../components/common/card-alert/CardAlert';
import {useIntl} from 'react-intl';
import {customerServiceLink} from '../../common/ApiUtils';
import {credentialRecoveryEmail} from '../../api/ForgotUserOrOrgIdApi';
import {maskedEmailAddress} from '../../common/ApiUtils';

function ForgotUserOrOrgIdEmail() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({email: ""});
  const intl = useIntl();
  const location = useLocation();
  const mainRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = intl.formatMessage({id: 'logon.ui.forgot.userid.orgid.email.step.page.title'});
    // Set focus to main element for screen reader announcement
    if (mainRef.current) {
      mainRef.current.focus();
    }

    if (location.state?.from !== '/login' && location.state?.from !== '/forgotPassword') {
      navigate('/');
    }
  }, [intl,navigate,location]);

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const showErrorOnPage = (error) => {
    setErrorMessage(error);
    setHasError(true);
  }

  const [errors, setErrors] = useState({
    email: {required: false, invalid: false},
  });

  const isValidEmail = (email) => {
    const emailRegEx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegEx.test(email);
  }

  const handleChange = (event) => {
    const {name, value} = event.target;

    setFormData((prevFormData) => ({...prevFormData, [name]: value}));

    //Clear error when user begins to type
    if (name === 'email') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: {required: false, invalid: false},
      }));
    }
  };

  //Validation for empty fields
  const validateFormData = (formData) => {
    return {
      email: {
        required: formData.email.trim() === '',
        invalid: formData.email.trim() !== '' && !isValidEmail(formData.email),
      }
    };
  };

  const handleSubmit = async (event) => {
    setIsLoading(true);
    setIsSubmitting(true);
    event.preventDefault();

    const newErrors = validateFormData(formData);
    setErrors(newErrors);

    //Submit the form
    if (!newErrors.email.required && !newErrors.email.invalid) {
      try {
        const response = await credentialRecoveryEmail(formData.email);
        sessionStorage.setItem("userEmail", maskedEmailAddress(formData.email));
        if (response.data) {
          if(response.data==="email.duplicate"){
            setFormData({email: ""});
            sessionStorage.setItem("previousPage",Constants.forgotUserOrOrgIdEmail);
            showErrorOnPage(Constants.forgotUserOrOrgIdDuplicateEmailError)
            setIsSubmitting(false);
          }
          else if(response.data==="email.inactive"){
            setFormData({email: ""});
            showErrorOnPage(Constants.inactiveUserErrorMessage)
            setIsSubmitting(false);
          }
          else if(response.data==="email.invalid"){
            setFormData({email: ""});
            showErrorOnPage(Constants.invalidUserEmailError)
            setIsSubmitting(false);
          }
          else {
            navigate(Constants.forgotUserOrOrgIdComplete, {state: {formData, from: Constants.forgotUserOrOrgIdEmail}});
          }
        } else if (response.error && response.error.detail) {
          setFormData({email: ""});
          showErrorOnPage(response.error.detail);
          setIsSubmitting(false);
        }
      } catch (error) {
        setIsSubmitting(false);
        throw error;
      }
      finally {
        setIsLoading(false);
      }
    }
    else {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  function handleNavigateToCommercialCard() {
    navigate('/forgotUserOrOrgIdCard', { state: { formData, from: '/forgotUserOrOrgIdEmail' } });
  }

  return (
      <main ref={mainRef} role="main" aria-label="Forgot User or Org ID Email" tabIndex="-1">
        <FlexLayout className={"page-content"}>
          {isLoading ? (
              <FlexItem className="spinner-wrapper">
                <Spinner data-testid="spinner-loading" aria-label="loading" role="status" size="medium" style={{marginLeft: "50%"}} />
              </FlexItem>
          ) : (
              <FlexItem className={`error-wrapper ${hasError ? 'visible' : ''}`}>
                <div>
                  <CardAlert status={"error"} setHasError={setHasError}>{errorMessage}</CardAlert>
                </div>
              </FlexItem>
          )}

          <FlexItem>
            <Card className="card-wrapper">
              <FlexItem className="card-content">
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
                  <StackLayout gap={4}>
                    <CardHeader heading={Constants.forgotUserOrOrgIdTitle} subheading={Constants.forgotUserOrOrgIdEmailStep}
                                style={{width: "71%"}} />
                    <FlexItem>
                      <Text>
                        {Constants.forgotUserOrOrgIdEmailStepInstructionHeader(customerServiceLink)}
                      </Text>
                    </FlexItem>
                    <FlexItem className="form-layout">
                      <FormField validationStatus={(errors.email.required || errors.email.invalid) ? "error" : undefined}>
                        <FormFieldLabel htmlFor="email">{Constants.emailAddressLabel}</FormFieldLabel>
                        <Input id="email" inputProps={{name: "email", maxLength:50, "aria-required": "true", "aria-describedby": errors.email ? "email-error" : undefined}} value={formData.email}
                               onChange={handleChange} />
                        {errors.email.required ?
                            <FormFieldHelperText id="email-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText> : (errors.email.invalid ?
                                <FormFieldHelperText id="email-error" role="alert">{Constants.invalidEmailError}</FormFieldHelperText> : "")}
                      </FormField>
                    </FlexItem>
                    <FlexItem>
                      <Link color="info" tabIndex={0} onClick={handleNavigateToCommercialCard} onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleNavigateToCommercialCard();
                        }
                      }}>
                        {Constants.forgotUserOrOrgIdEmailStepUseCardInfo}
                      </Link>
                    </FlexItem>
                    <PageNavigation/>
                  </StackLayout>
                  </fieldset>
                </form>
              </FlexItem>
            </Card>
          </FlexItem>
        </FlexLayout>
      </main>
  );
}

export default ForgotUserOrOrgIdEmail;

